import {
  DEFAULT_QUOTE_VALIDITY_DAYS,
  FINANCING_LABELS,
  FinancingType,
  quoteFinalPrice,
  type UnitDto,
} from '@performance-crm/shared';
import { Loader2 } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { NextStepFields } from '@/components/leads/NextStepFields';
import { emptyNextStep, toNextStep, type NextStepForm } from '@/components/leads/nextStep';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/toast-context';
import { ApiRequestError } from '@/lib/api';
import {
  describeProjectCommercial,
  fmt,
  fromLocalInput,
  toDateInput,
  todayInTz,
} from '@/lib/format';
import { useCompanyTimezone } from '@/lib/useCompanyTimezone';
import { ContactDistrictField } from '@/components/leads/ContactDistrictField';
import { occurredAtNow, occurredAtToDate } from '@/components/leads/occurredAt';
import { OccurredAtField } from '@/components/leads/OccurredAtField';
import { useCatalogs, useCreateQuote, useLead, useProjects, useUpdateContact } from '@/lib/queries';
import { UnitPicker } from './UnitPicker';

/** Proforma completa desde la ficha: unidad (o sin unidad), lista, descuento, financiamiento, vigencia y siguiente paso. */
export function QuoteDialog({
  leadId,
  companyId,
  projectId,
  open,
  onOpenChange,
}: {
  leadId: string;
  companyId: string;
  projectId: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const { data: catalogs } = useCatalogs(companyId);
  const { data: lead } = useLead(leadId);
  const { data: projects } = useProjects();
  const commercialHint = describeProjectCommercial(
    projects?.find((p) => p.id === projectId)?.commercial,
  );
  // Sin unidad el precio arranca en la moneda de la empresa; con unidad va en la de la unidad (P9).
  const companyCurrency = catalogs?.settings.currency ?? 'USD';
  const mutation = useCreateQuote(leadId);
  const updateContact = useUpdateContact(leadId);
  const toast = useToast();
  const tz = useCompanyTimezone(companyId);
  const [district, setDistrict] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [next, setNext] = useState<NextStepForm>(emptyNextStep(tz));
  const [unitIds, setUnitIds] = useState<string[]>([]);
  const [unit, setUnit] = useState<UnitDto | null>(null);
  // Vigencia por defecto: hoy + los días de vigencia, contados en la zona de la empresa.
  const defaultExpiresAt = () =>
    toDateInput(new Date(Date.now() + DEFAULT_QUOTE_VALIDITY_DAYS * 86_400_000).toISOString(), tz);
  const [f, setF] = useState({
    priceList: '',
    discount: '0',
    currency: 'USD',
    financing: '' as '' | FinancingType,
    expiresAt: defaultExpiresAt(),
    notes: '',
    withVisit: false,
    visitAt: occurredAtNow(tz),
  });
  const set = (patch: Partial<typeof f>) => setF((p) => ({ ...p, ...patch }));
  // Las fechas por defecto se calculan al ABRIR, no al montar (ux-transversal-21): una ficha abierta de un día
  // para otro no propone la fecha vieja.
  useEffect(() => {
    if (open) {
      setError(null);
      setNext(emptyNextStep(tz));
      setUnitIds([]);
      setUnit(null);
      setF((p) => ({
        ...p,
        priceList: '',
        discount: '0',
        currency: companyCurrency,
        financing: '',
        notes: '',
        expiresAt: defaultExpiresAt(),
        withVisit: false,
        visitAt: occurredAtNow(tz),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tz, companyCurrency]);
  useEffect(() => {
    if (open) setDistrict(lead?.contact.district ?? '');
  }, [open, lead?.contact.district]);
  const priceList = Number(f.priceList) || 0;
  const discount = Number(f.discount) || 0;
  const finalPrice = priceList ? quoteFinalPrice(priceList, discount) : 0;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      // El distrito vive en el contacto: se guarda antes de emitir si cambió.
      if (lead && district.trim() !== (lead.contact.district ?? ''))
        await updateContact.mutateAsync({
          contactId: lead.contact.id,
          input: { district: district.trim() || null },
        });
      const r = await mutation.mutateAsync({
        unitId: unit?.id ?? null,
        priceList: priceList || null,
        discount,
        price: finalPrice || null,
        currency: (unit ? unit.currency : f.currency) as 'USD' | 'PEN',
        financingType: f.financing || null,
        // Vigente hasta el final de ese día en la zona de la empresa.
        expiresAt: f.expiresAt ? new Date(fromLocalInput(`${f.expiresAt}T23:59`, tz)) : null,
        notes: f.notes || null,
        // La visita es un hecho propio: se registra como VISITA con esta fecha (no con la de la proforma).
        visitAt: f.withVisit && f.visitAt ? occurredAtToDate(f.visitAt, tz) : null,
        next: toNextStep(next, tz),
      });
      toast.success(`Proforma ${r.code} emitida`);
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'No se pudo emitir la proforma');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Nueva proforma</DialogTitle>
            <DialogDescription>
              Elige una unidad disponible (o deja la proforma sin unidad) e indica precio, descuento
              y vigencia. El lead pasa a la etapa Proforma.
            </DialogDescription>
            {commercialHint && (
              <p className="text-xs" data-testid="quote-commercial-hint">
                Ficha comercial: {commercialHint}
              </p>
            )}
          </DialogHeader>
          <div className="space-y-1">
            <Label>Unidad de interés</Label>
            <UnitPicker
              projectId={projectId}
              value={unitIds}
              idPrefix="quote"
              onChange={(ids, units) => {
                setUnitIds(ids);
                const u = units[0] ?? null;
                setUnit(u);
                // Con unidad, el precio va en su moneda (el selector queda bloqueado); sin unidad, la de la empresa.
                set(
                  u
                    ? {
                        priceList: String(Number(u.priceList ?? u.priceSale ?? 0) || ''),
                        currency: u.currency,
                      }
                    : { currency: companyCurrency },
                );
              }}
            />
            {unit && (
              <p className="text-muted-foreground text-xs">
                {unit.code} · lista {fmt.money(unit.priceList, unit.currency)} · venta{' '}
                {fmt.money(unit.priceSale, unit.currency)}
              </p>
            )}
          </div>
          <div className="grid gap-3 sm:grid-cols-4">
            <div className="space-y-1">
              <Label htmlFor="q-list">Precio de lista</Label>
              <Input
                id="q-list"
                type="number"
                min={0}
                step="0.01"
                value={f.priceList}
                onChange={(e) => set({ priceList: e.target.value })}
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="q-discount">Descuento</Label>
              <Input
                id="q-discount"
                type="number"
                min={0}
                step="0.01"
                value={f.discount}
                onChange={(e) => set({ discount: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="q-currency">Moneda</Label>
              <Select
                id="q-currency"
                value={unit ? unit.currency : f.currency}
                disabled={!!unit}
                onChange={(e) => set({ currency: e.target.value })}
              >
                <option value="USD">US$</option>
                <option value="PEN">S/</option>
              </Select>
              {unit && <p className="text-muted-foreground text-xs">La de la unidad.</p>}
            </div>
            <div className="space-y-1">
              <Label>Precio final</Label>
              <p className="flex h-10 items-center text-sm font-semibold" data-testid="quote-final">
                {fmt.money(finalPrice, unit ? unit.currency : f.currency)}
              </p>
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="q-fin">Financiamiento</Label>
              <Select
                id="q-fin"
                value={f.financing}
                onChange={(e) => set({ financing: e.target.value as '' | FinancingType })}
              >
                <option value="">—</option>
                {Object.values(FinancingType).map((v) => (
                  <option key={v} value={v}>
                    {FINANCING_LABELS[v]}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="q-expires">Vigente hasta</Label>
              <Input
                id="q-expires"
                type="date"
                value={f.expiresAt}
                min={todayInTz(tz)}
                onChange={(e) => set({ expiresAt: e.target.value })}
              />
              {f.expiresAt && f.expiresAt < todayInTz(tz) && (
                <p className="text-destructive text-xs" data-testid="quote-expired-hint">
                  Esa vigencia ya pasó: la proforma nacería vencida.
                </p>
              )}
            </div>
            <div className="space-y-1 sm:col-span-4">
              <Label htmlFor="q-notes">Notas</Label>
              <Textarea
                id="q-notes"
                rows={2}
                value={f.notes}
                onChange={(e) => set({ notes: e.target.value })}
                placeholder="Condiciones, promociones, unidades adicionales…"
              />
            </div>
            <div className="sm:col-span-2">
              <ContactDistrictField id="q-district" value={district} onChange={setDistrict} />
            </div>
            <label className="flex items-center gap-2 text-sm sm:col-span-2">
              <input
                type="checkbox"
                checked={f.withVisit}
                onChange={(e) => set({ withVisit: e.target.checked })}
                className="h-4 w-4"
              />
              Se presentó en sala (cuenta como visita)
            </label>
            {f.withVisit && (
              <OccurredAtField
                id="q-visit"
                className="sm:col-span-2"
                label="¿Cuándo fue la visita?"
                value={f.visitAt}
                onChange={(v) => set({ visitAt: v })}
                tz={tz}
              />
            )}
          </div>
          <NextStepFields
            value={next}
            onChange={setNext}
            desistReasons={catalogs?.desistReasons ?? []}
            idPrefix="q-next"
          />
          {error && (
            <p
              role="alert"
              className="bg-destructive/10 text-destructive rounded-md px-3 py-2 text-sm"
            >
              {error}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending} data-testid="quote-submit">
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Emitir proforma
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
